import api, { unwrap } from './client'
import type {
  BuildEvidence,
  CodeGraph,
  CodeGraphStructure,
  GithubInstallation,
  GithubInstallationSetup,
  GithubRepositorySettings,
  Paginated,
  PaginatedRepoRules,
  PaginationFilters,
  PendingRepoRule,
  RepoLesson,
  RepoRule,
  RepoRuleDraft,
  RepoScan,
  RepoScanFilters,
  ReviewRun,
  ReviewRunEvent,
  ReviewRunFilters,
  RuleFinding,
  RuleFindingFilters,
  RuleFindingTrend,
  RuleFindingTrendFilters,
  ScanEstimate,
} from '@/types/models'

export const githubApi = {
  async installUrl(): Promise<string> {
    const { url } = await unwrap(api.get<{ data: { url: string } }>('/integrations/github/install-url'))
    return url
  },

  getInstallationSetup(setupId: string): Promise<GithubInstallationSetup> {
    return unwrap(api.get<{ data: GithubInstallationSetup }>(`/integrations/github/setups/${setupId}`))
  },

  async installations(): Promise<GithubInstallation[]> {
    return (await unwrap(api.get<{ data: GithubInstallation[] }>('/integrations/github/installations'))) ?? []
  },

  disconnect(id: string): Promise<unknown> {
    return api.delete(`/integrations/github/installations/${id}`)
  },

  updateInstallation(
    id: string,
    payload: Partial<Pick<GithubInstallation, 'openrouter_api_key' | 'jev_enabled'>>,
  ): Promise<GithubInstallation> {
    return unwrap(
      api.patch<{ data: GithubInstallation }>(`/integrations/github/installations/${id}`, payload),
    )
  },

  syncRepositories(id: string): Promise<unknown> {
    return api.post(`/integrations/github/installations/${id}/repositories/sync`)
  },

  // Runs as a queued job server-side (fans out one GitHub call per repo's
  // open PRs, too slow for a synchronous request) — this just queues it;
  // callers refetch the review runs list once it completes.
  syncReviewRuns(id: string): Promise<unknown> {
    return api.post(`/integrations/github/installations/${id}/review-runs/sync`)
  },

  starRepository(id: string, repoFullName: string, isStarred: boolean): Promise<unknown> {
    return api.post(`/integrations/github/installations/${id}/repositories/star`, {
      repo_full_name: repoFullName,
      is_starred: isStarred,
    })
  },

  async repositorySettings(installationId: string): Promise<GithubRepositorySettings[]> {
    return (
      (await unwrap(
        api.get<{ data: GithubRepositorySettings[] }>(
          `/integrations/github/installations/${installationId}/repository-settings`,
        ),
      )) ?? []
    )
  },

  updateRepositorySettings(
    installationId: string,
    payload: { repo_full_name: string } & Partial<Omit<GithubRepositorySettings, 'repo_full_name'>>,
  ): Promise<GithubRepositorySettings> {
    return unwrap(
      api.patch<{ data: GithubRepositorySettings }>(
        `/integrations/github/installations/${installationId}/repository-settings`,
        payload,
      ),
    )
  },

  testSlackNotification(installationId: string, repoFullName: string): Promise<unknown> {
    return api.post(
      `/integrations/github/installations/${installationId}/repository-settings/test-slack-notification`,
      { repo_full_name: repoFullName },
    )
  },

  async listRepoLessons(installationId: string, repositorySettingsId: string): Promise<RepoLesson[]> {
    return (
      (await unwrap(
        api.get<{ data: RepoLesson[] }>(`${repositorySettingsBase(installationId, repositorySettingsId)}/lessons`),
      )) ?? []
    )
  },

  async listRepoRules(
    installationId: string,
    repositorySettingsId: string,
    page?: number,
    sort?: string,
    title?: string,
  ): Promise<PaginatedRepoRules> {
    const params: Record<string, number | string> = {}
    if (page) params.page = page
    // e.g. 'disagree_rate' or '-disagree_rate' — see RepoRuleIndexQuery.
    if (sort) params.sort = sort
    // Partial match against the rule title — see RepoRuleIndexQuery.
    if (title) params['filter[title]'] = title

    const { data } = await api.get<PaginatedRepoRules>(`${repoRulesBase(installationId, repositorySettingsId)}`, {
      params,
    })

    return data
  },

  createRepoRule(
    installationId: string,
    repositorySettingsId: string,
    payload: Pick<RepoRule, 'title' | 'description' | 'bad_example' | 'good_example' | 'language'>,
  ): Promise<RepoRule> {
    return unwrap(api.post<{ data: RepoRule }>(`${repoRulesBase(installationId, repositorySettingsId)}`, payload))
  },

  getRepoRule(installationId: string, repositorySettingsId: string, ruleId: string): Promise<RepoRule> {
    return unwrap(api.get<{ data: RepoRule }>(`${repoRulesBase(installationId, repositorySettingsId)}/${ruleId}`))
  },

  improveRepoRule(
    installationId: string,
    repositorySettingsId: string,
    ruleId: string,
    suggestion: string,
  ): Promise<unknown> {
    return api.post(`${repoRulesBase(installationId, repositorySettingsId)}/${ruleId}/improve`, { suggestion })
  },

  // Copies the rule onto each target repo as its own independent
  // source=Imported/status=PendingReview row — see ApplyRepoRuleController.
  async applyRepoRule(
    installationId: string,
    repositorySettingsId: string,
    ruleId: string,
    targetRepositorySettingsIds: string[],
  ): Promise<RepoRule[]> {
    return (
      (await unwrap(
        api.post<{ data: RepoRule[] }>(`${repoRulesBase(installationId, repositorySettingsId)}/${ruleId}/apply`, {
          repository_settings_ids: targetRepositorySettingsIds,
        }),
      )) ?? []
    )
  },

  updateRepoRule(
    installationId: string,
    repositorySettingsId: string,
    ruleId: string,
    payload: Partial<Pick<RepoRule, 'title' | 'description' | 'bad_example' | 'good_example' | 'language' | 'status'>>,
  ): Promise<RepoRule> {
    return unwrap(
      api.patch<{ data: RepoRule }>(`${repoRulesBase(installationId, repositorySettingsId)}/${ruleId}`, payload),
    )
  },

  deleteRepoRule(installationId: string, repositorySettingsId: string, ruleId: string): Promise<unknown> {
    return api.delete(`${repoRulesBase(installationId, repositorySettingsId)}/${ruleId}`)
  },

  generateRepoRule(installationId: string, repositorySettingsId: string, description: string): Promise<RepoRuleDraft> {
    return unwrap(
      api.post<{ data: RepoRuleDraft }>(`${repoRulesBase(installationId, repositorySettingsId)}/generate`, {
        description,
      }),
    )
  },

  getRepoRuleDraft(installationId: string, repositorySettingsId: string, draftId: string): Promise<RepoRuleDraft> {
    return unwrap(
      api.get<{ data: RepoRuleDraft }>(`${repoRulesBase(installationId, repositorySettingsId)}/generate/${draftId}`),
    )
  },

  async listRepoScans(installationId: string, repositorySettingsId: string): Promise<RepoScan[]> {
    return (
      (await unwrap(
        api.get<{ data: RepoScan[] }>(`${repositorySettingsBase(installationId, repositorySettingsId)}/scans`),
      )) ?? []
    )
  },

  startRepoScan(installationId: string, repositorySettingsId: string, scope?: string[]): Promise<RepoScan> {
    return unwrap(
      api.post<{ data: RepoScan }>(
        `${repositorySettingsBase(installationId, repositorySettingsId)}/scans`,
        scope && scope.length > 0 ? { scope } : {},
      ),
    )
  },

  getScanEstimate(installationId: string, repositorySettingsId: string, scope?: string[]): Promise<ScanEstimate> {
    return unwrap(
      api.post<{ data: ScanEstimate }>(
        `${repositorySettingsBase(installationId, repositorySettingsId)}/scans/estimate`,
        scope && scope.length > 0 ? { scope } : {},
      ),
    )
  },

  getCodeGraph(installationId: string, repositorySettingsId: string): Promise<CodeGraph> {
    return unwrap(
      api.get<{ data: CodeGraph }>(`${repositorySettingsBase(installationId, repositorySettingsId)}/code-graph`),
    )
  },

  triggerCodeGraph(installationId: string, repositorySettingsId: string): Promise<CodeGraph> {
    return unwrap(
      api.post<{ data: CodeGraph }>(`${repositorySettingsBase(installationId, repositorySettingsId)}/code-graph`),
    )
  },

  getCodeGraphStructure(installationId: string, repositorySettingsId: string): Promise<CodeGraphStructure> {
    return unwrap(
      api.get<{ data: CodeGraphStructure }>(
        `${repositorySettingsBase(installationId, repositorySettingsId)}/code-graph/structure`,
      ),
    )
  },

  backfillPullRequestReviews(installationId: string, repositorySettingsId: string): Promise<unknown> {
    return api.post(`${repositorySettingsBase(installationId, repositorySettingsId)}/reviews/backfill`)
  },

  async exportRepoRules(installationId: string, repositorySettingsId: string): Promise<Blob> {
    const { data } = await api.get(`${repoRulesBase(installationId, repositorySettingsId)}/export`, {
      responseType: 'blob',
    })
    return data
  },

  async importRepoRules(installationId: string, repositorySettingsId: string, payload: unknown): Promise<RepoRule[]> {
    return (
      (await unwrap(
        api.post<{ data: RepoRule[] }>(`${repoRulesBase(installationId, repositorySettingsId)}/import`, payload),
      )) ?? []
    )
  },

  // The import runs as a queued CLI job server-side (too slow for a
  // synchronous request) — this just queues it; callers poll listRepoRules
  // for the new PendingReview rows.
  async importRulesFromSkill(installationId: string, repositorySettingsId: string, skill: 'otwell' | 'perfecto'): Promise<void> {
    await api.post(`${repoRulesBase(installationId, repositorySettingsId)}/skill-import`, { skill })
  },

  async exportRepositoryConfig(installationId: string, repositorySettingsId: string): Promise<Blob> {
    const { data } = await api.get(`${repositorySettingsBase(installationId, repositorySettingsId)}/export`, {
      responseType: 'blob',
    })
    return data
  },

  importRepositoryConfig(
    installationId: string,
    repositorySettingsId: string,
    payload: unknown,
  ): Promise<GithubRepositorySettings> {
    return unwrap(
      api.post<{ data: GithubRepositorySettings }>(
        `${repositorySettingsBase(installationId, repositorySettingsId)}/import`,
        payload,
      ),
    )
  },

  async listReviewRuns(installationId: string, filters: ReviewRunFilters = {}): Promise<Paginated<ReviewRun>> {
    const params: Record<string, string | number> = {}
    if (filters.status) params['filter[status]'] = filters.status
    if (filters.repo_full_name) params['filter[repo_full_name]'] = filters.repo_full_name
    if (filters.created_after) params['filter[created_after]'] = filters.created_after
    if (filters.created_before) params['filter[created_before]'] = filters.created_before
    if (filters.page) params.page = filters.page
    if (filters.per_page) params.per_page = filters.per_page

    const { data } = await api.get<Paginated<ReviewRun>>(
      `/integrations/github/installations/${installationId}/review-runs`,
      { params },
    )
    return data
  },

  // Flips the run to Cancelled and concludes its GitHub check run — see
  // CancelReviewRunAction. 409s if the run has already reached a terminal
  // status by the time this lands.
  cancelReviewRun(installationId: string, reviewRunId: string): Promise<ReviewRun> {
    return unwrap(
      api.post<{ data: ReviewRun }>(
        `/integrations/github/installations/${installationId}/review-runs/${reviewRunId}/cancel`,
      ),
    )
  },

  getReviewRun(installationId: string, reviewRunId: string): Promise<ReviewRun> {
    return unwrap(
      api.get<{ data: ReviewRun }>(`/integrations/github/installations/${installationId}/review-runs/${reviewRunId}`),
    )
  },

  // Real test output, independent-verification verdicts, and QA
  // screenshots/gifs captured for the build this review run triggered — see
  // linearTasksApi.getBuildEvidence for the LinearTask-side equivalent; same
  // response shape, both bundled by the backend's BuildEvidenceResource.
  getReviewRunBuildEvidence(installationId: string, reviewRunId: string): Promise<BuildEvidence> {
    return unwrap(
      api.get<{ data: BuildEvidence }>(
        `/integrations/github/installations/${installationId}/review-runs/${reviewRunId}/build-evidence`,
      ),
    )
  },

  // The structured, per-tool-call log for a review run's timeline page.
  async getReviewRunEvents(installationId: string, reviewRunId: string): Promise<ReviewRunEvent[]> {
    return (
      (await unwrap(
        api.get<{ data: ReviewRunEvent[] }>(
          `/integrations/github/installations/${installationId}/review-runs/${reviewRunId}/events`,
        ),
      )) ?? []
    )
  },

  async listInstallationRepoScans(installationId: string, filters: RepoScanFilters = {}): Promise<Paginated<RepoScan>> {
    const params: Record<string, string | number> = {}
    if (filters.status) params['filter[status]'] = filters.status
    if (filters.page) params.page = filters.page
    if (filters.per_page) params.per_page = filters.per_page

    const { data } = await api.get<Paginated<RepoScan>>(
      `/integrations/github/installations/${installationId}/repo-scans`,
      { params },
    )
    return data
  },

  async listPendingRepoRules(filters: PaginationFilters = {}): Promise<Paginated<PendingRepoRule>> {
    const params: Record<string, number> = {}
    if (filters.page) params.page = filters.page
    if (filters.per_page) params.per_page = filters.per_page

    const { data } = await api.get<Paginated<PendingRepoRule>>('/integrations/github/rules/pending-review', { params })
    return data
  },

  bulkApproveRepoRules(ruleIds: string[]): Promise<unknown> {
    return api.post('/integrations/github/rules/pending-review/approve', { rule_ids: ruleIds })
  },

  bulkRejectRepoRules(ruleIds: string[]): Promise<unknown> {
    return api.post('/integrations/github/rules/pending-review/reject', { rule_ids: ruleIds })
  },

  async listRuleFindingTrends(filters: RuleFindingTrendFilters = {}): Promise<RuleFindingTrend[]> {
    const params: Record<string, string | number> = {}
    if (filters.repo_full_name) params.repo_full_name = filters.repo_full_name
    if (filters.status) params.status = filters.status

    return (await unwrap(api.get<{ data: RuleFindingTrend[] }>('/integrations/github/rules/finding-trends', { params }))) ?? []
  },

  async listRuleFindings(filters: RuleFindingFilters = {}): Promise<Paginated<RuleFinding>> {
    const params: Record<string, string | number> = {}
    if (filters.rule_id) params['filter[rule_id]'] = filters.rule_id
    if (filters.repo_full_name) params['filter[repo_full_name]'] = filters.repo_full_name
    if (filters.created_after) params['filter[created_after]'] = filters.created_after
    if (filters.created_before) params['filter[created_before]'] = filters.created_before
    if (filters.sort) params.sort = filters.sort
    if (filters.page) params.page = filters.page
    if (filters.per_page) params.per_page = filters.per_page

    const { data } = await api.get<Paginated<RuleFinding>>('/integrations/github/rules/findings', { params })
    return data
  },
}

function repositorySettingsBase(installationId: string, repositorySettingsId: string): string {
  return `/integrations/github/installations/${installationId}/repository-settings/${repositorySettingsId}`
}

function repoRulesBase(installationId: string, repositorySettingsId: string): string {
  return `${repositorySettingsBase(installationId, repositorySettingsId)}/rules`
}
